import Document from '../model/Document';
import m from './module';
import { createError } from '../error';
import { getProvisionalIdentifierForType } from '../util';
import heic2any from "heic2any";
import * as piexif from 'piexif-ts';
import { Exifr } from 'exifr';

const documentPath = '/document/{{documents_id}}';
const documentThumbnailPath = '/document/{{documents_id}}/thumb';
const documentSavePath = '/document/save';

const uploadMaxAttempts = 3;
const uploadRetryBaseDelayMs = 1000;

const retryableUploadErrors = [
  'IncompleteUpload',
  'EmptyUpload',
  'UploadStorageFailed',
];

// A bare 500 is deliberately not retryable. The upload endpoint can fail after the
// document row and file are stored, because auto-tagging runs once handleUpload has
// returned, so retrying one would attach a second copy of the same photo. The named
// errors above are safe by contrast: each is thrown before anything is written, or
// after the row has been deleted again.
const retryableUploadStatuses = [503];

const dateFields = [
  'creation_date',
  'created_at',
  'updated_at',
  'file_modified_timestamp',
];

class DocumentService {
  constructor(
    $q,
    $log,
    $timeout,
    APIService,
    LogService,
    DocumentResizeService,
    Upload,
    $window
  ) {
    this.$q = $q;
    this.$log = $log;
    this.$timeout = $timeout;
    this.$window = $window;
    this.APIService = APIService;
    this.LogService = LogService;
    this.DocumentResizeService = DocumentResizeService;
    this.Upload = Upload;
    this._objectsByIdMap = {};
    this._thinDocumentsByIdMap = {};
  }

  create(data) {
    const promises = [];
    data = data || {};
    if (!data.id) {
      data.id = getProvisionalIdentifierForType("document");
    }

    // Convert dates
    for (const field of dateFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.parseDateWithServerTimezone(data[field]);
      }
    }

    return this.$q.all(promises)
      .then(() => {
        const model = new Document(data);
        this._objectsByIdMap[data.id] = model;
        return model;
      })
  }

  createWithLocalFile(file, data = null) {
    this.LogService.log("createWithLocalFile() file=", file, "data=", data);

    data = data || {};
    data.name = data.name || file.name;

    // First create document with provided data
    return this.create(data)
      .then(document => {
        // Read dataURL for this file
        this.LogService.log("createWithLocalFile() created document, now reading dataURL");
        return this._getDataURLForLocalFile(file)
          .then(async dataURL => {
            document.fileDataURL = dataURL
            if (document.fileDataURL.includes('image/heic')) {
              document.fileDataURL = await this._convertLocalImageToJpeg(file)
              document.name += '.jpeg'
            }
            return document
          })
          .catch(err => {
            this.LogService.error("Error getting dataURL for local file:", err);
            return this.$q.reject(err);
          });
      });
  }

  async _convertLocalImageToJpeg(file) {
    try {
      const buffer = await file.arrayBuffer();
      const jpegBlob = await this.convertHeicToJpeg(buffer);
      return await this._getDataURLForLocalFile(jpegBlob);
    } catch (e) {
      this.LogService.error('Error converting file to JPEG with EXIF preservation:', e);
      try {
        // Fallback: convert without EXIF preservation rather than running heic2any again
        // only if the error was in EXIF processing, not in heic2any itself
        const jpegBlob = await heic2any({
          blob: file,
          toType: "image/jpeg",
          quality: 1,
        });
        return await this._getDataURLForLocalFile(jpegBlob);
      } catch (e2) {
        this.LogService.error('Error in fallback HEIC conversion:', e2);
      }
    }
    return null;
  }

  loadExif(fileDataURL) {
    const exif = piexif.load(fileDataURL)
    return exif
  }

  dumpExif(exif) {
    return piexif.dump(exif)
  }

  insertExif (exif, fileDataURL) {
    return piexif.insert(exif, fileDataURL)
  }

  resizeDocument(document) {
    const shouldResizeImages = this.DocumentResizeService.shouldResizeImages
    const maxImageDimension = this.DocumentResizeService.maxImageDimension
    const imageQuality = this.DocumentResizeService.imageQuality
    let deferred = this.$q.defer()
    let canvas = this.$window.document.createElement('canvas')
    let img = this.$window.document.createElement('img')
    let type = 'image/jpeg'
    let quality = imageQuality

    img.onload = () => {
      this.LogService.log("Image onload")

      try {
        // Check early exits BEFORE doing any expensive work
        if (this.documentIsNotImage(document)) {
          deferred.resolve(document);
          return;
        }

        const needsResize = shouldResizeImages
          && (img.width > maxImageDimension || img.height > maxImageDimension);

        if (!needsResize) {
          deferred.resolve(document);
          return;
        }

        // Image needs resizing — yield to the event loop before heavy work
        // so the UI can update (e.g. show a spinner)
        setTimeout(() => {
          try {
            // Step 1: Read EXIF (only when we actually need to resize)
            let exifData;
            try {
              exifData = this.loadExif(document.fileDataURL)
            } catch(e) {
              exifData = null
            }

            // Step 2: Resize via canvas
            let ctx = canvas.getContext('2d')
            let hRatio = maxImageDimension / img.width
            let vRatio = maxImageDimension / img.height
            let ratio = Math.min(hRatio, vRatio)

            let resizedWidth = img.width * ratio
            let resizedHeight = img.height * ratio
            canvas.width = resizedWidth
            canvas.height = resizedHeight

            ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, resizedWidth, resizedHeight)

            // Step 3: Export to data URL — yield again after this heavy operation
            const fileDataURL = canvas.toDataURL(type, quality)

            // Step 4: Re-insert EXIF if we had it
            setTimeout(() => {
              try {
                if (exifData) {
                  const exifBinary = this.dumpExif(exifData)
                  const inserted = this.insertExif(exifBinary, fileDataURL)
                  document.fileDataURL = inserted
                } else {
                  document.fileDataURL = fileDataURL
                }

                // Free canvas memory
                canvas.width = 0
                canvas.height = 0

                deferred.resolve(document)
              } catch (e) {
                deferred.reject(e)
              }
            }, 0)
          } catch (e) {
            deferred.reject(e)
          }
        }, 0)
      } catch (e) {
        deferred.reject(e)
      }
    }

    img.onerror = (e) => {
      this.LogService.error('Error loading image for resize', e)
      deferred.reject(e)
    }

    this.LogService.log("Attempting to load image");
    img.src = document.fileDataURL

    return deferred.promise
      .then(document => {
        this.LogService.log("Successfully resized document")
        return document;
      })
      .catch(err => {
        this.LogService.warn("Error resizing document:", err)
        return document;
      });
  }

  documentIsNotImage(document){
    let type = null;
    let output = false;
    if (document.fileDataURL) {
      type = document.fileDataURL.substring(document.fileDataURL.indexOf(":") + 1, document.fileDataURL.indexOf("/"));
    }
    if (type && type != 'image') {
        output = true;
    }
    return output;
  }


  getThinById(id) {
    if (!this._thinDocumentsByIdMap[id]) {
      this._thinDocumentsByIdMap[id] = this.create({id})
        .then(doc => {
          return doc;
        });
    }
    return this._thinDocumentsByIdMap[id];
  }

  serialize(doc) {
    let data = {...doc};

    // Convert dates
    for (let field of dateFields) {
      if (data[field]) {
        data[field] = this.DateTimeService.serializeDateTimeForAPI(data[field]);
      }
    }
    return data;
  }

  documentURLForDocumentId(documentId) {
    const path = documentPath.replace('{{documents_id}}', documentId);
    return this.APIService.convertPathToURL(path);
  }

  documentThumbnailURLForDocumentId(documentId, options = null) {
    options = options || {};
    let path = documentThumbnailPath.replace('{{documents_id}}', documentId);

    // Include auth token as query param for img src requests that can't send Authorization header
    const token = this.APIService.getAuthToken();
    if (token) {
      options.token = token;
    }

    path = this.APIService.addURLParamsFromObject(path, options);
    return this.APIService.convertPathToURL(path);
  }

  upload(document, url, postData = null, fileKey = 'file') {
    if (!document.fileDataURL) {
      return this.$q.reject("Missing fileDataURL");
    }

    const blob = this.Upload.dataUrltoBlob(document.fileDataURL, document.name);
    if (!blob || !blob.size) {
      this.LogService.error("Refusing to upload an empty blob for document:", document.id);
      return this.$q.reject(createError({
        error: 'EmptyDocument',
        message: 'The photo could not be prepared for upload. Please retake it.',
      }));
    }

    postData = postData || {};
    postData[fileKey] = blob;

    return this._uploadWithRetries(url, postData, 1);
  }

  /**
   * Posts the document, retrying the failures another attempt could actually get past.
   */
  _uploadWithRetries(url, postData, attempt) {
    return this.Upload.upload({
      url,
      data: postData,
    })
      .catch(response => {
        if (attempt >= uploadMaxAttempts || !this._isRetryableUploadFailure(response)) {
          return this.$q.reject(response);
        }

        const delay = uploadRetryBaseDelayMs * Math.pow(2, attempt - 1);
        this.LogService.log(
          `Document upload attempt ${attempt} of ${uploadMaxAttempts} failed; retrying in ${delay}ms.`,
          response
        );

        return this.$timeout(() => {}, delay)
          .then(() => this._uploadWithRetries(url, postData, attempt + 1));
      });
  }

  /**
   * True when the upload failed for a reason that is not about the file itself.
   */
  _isRetryableUploadFailure(response) {
    const status = response && response.status;

    // No response came back: the connection dropped, the request was aborted, or the device went offline mid-body
    if (!status || status <= 0) {
      return true;
    }

    if (retryableUploadStatuses.indexOf(status) !== -1) {
      return true;
    }

    const error = response.data && response.data.error;
    return retryableUploadErrors.indexOf(error) !== -1;
  }

  save(document,postData = null, fileKey='file') {
    return this.upload(document, documentSavePath, postData, fileKey);
  }

  /**
   * Wrap FileReader and return promise
   */
  _getDataURLForLocalFile(file) {
    let deferred = this.$q.defer();
    let reader = new FileReader();

    reader.onload = () => {
      deferred.resolve(reader.result);
    };
    reader.onerror = () => {
      this.LogService.error("FileReader onerror; rejecting with result:", reader.error);
      deferred.reject(reader.error);
    };
    reader.readAsDataURL(file);

    return deferred.promise;
  }

  getEventDocuments(path, events_id,staffing_users_id) {
    if (!staffing_users_id) {
      staffing_users_id = 0;
    }

    const url = this.APIService.convertPathToURL(path);
    const config = {
      force: true,
      params: {
        events_id,
        staffing_users_id,
      },
    };
    return this.APIService.get(url, config)
      .then(response => {
        return response.data;
      });
  }

  removeFromServer(path, document_ids) {
    const url = this.APIService.convertPathToURL(path);
    const config = {
      ids: document_ids,
    };
    return this.APIService.post(url, config);
  }

  toArray(input) {
    if (typeof input === 'object') return Array.from(input)
    else if (typeof input === 'undefined') return []
    else return [input]
  }

  toRational(input) {
    return (this.toArray(input)).map(Number).map((dec) => {
      if (!isFinite(dec) || dec === 0) {
        return [0, 1];
      }

      const sign = dec < 0 ? -1 : 1;
      const absValue = Math.abs(dec);

      if (Number.isInteger(absValue) || absValue > 10000000) {
        const numerator = sign * Math.round(absValue);
        if (numerator > 0x7fffffff || -numerator > 0x7fffffff) {
          return [Math.round(dec), 0];
        }
        return [numerator, 1];
      }

      // Continued fraction algorithm — converges in O(log n) steps
      const MAX_DENOMINATOR = 10000;
      let p0 = 0, q0 = 1, p1 = 1, q1 = 0;
      let x = absValue;

      while (true) {
        const a = Math.floor(x);
        const p2 = a * p1 + p0;
        const q2 = a * q1 + q0;

        if (q2 > MAX_DENOMINATOR) {
          break;
        }

        p0 = p1; q0 = q1;
        p1 = p2; q1 = q2;

        const remainder = x - a;
        if (remainder < 1e-10) {
          break;
        }
        x = 1.0 / remainder;
      }

      const finalNumerator = sign * p1;

      if (finalNumerator > 0x7fffffff || -finalNumerator > 0x7fffffff) {
        return [Math.round(dec), 0];
      }

      return [finalNumerator, q1];
    });
  }

  toAscii(input) {
    if (typeof input === 'string') return input
    return new TextDecoder('utf-8').decode(new Uint8Array(this.toArray(input)))
  }

  formatTypes(
    tag, // 'Image' | '0th' | 'Exif' | 'Interop' | 'GPS' | '1st',
    raw // Record<string, unknown>
  ) {
    const output = {}
    for (const [key, value] of Object.entries(raw)) {
      let type
      try {
        type = piexif.Tags[tag][Number(key)].type
      } catch (e) {
        throw new Error(`EXIF 2.32 (${tag}) does not have the tag ${key}`)
      }
      switch (type) {
        case piexif.Types.Rational:
        case piexif.Types.SRational:
          output[key] = this.toRational(value)
          break
        case piexif.Types.Short:
        case piexif.Types.Long:
        case piexif.Types.SLong:
          output[key] = this.toArray(value)
          break
        case piexif.Types.Ascii:
        case piexif.Types.Undefined:
          output[key] = this.toAscii(value)
          break
        case piexif.Types.Byte:
          output[key] = this.toArray(value)
          break
        default:
          throw new Error(`Unexpected EXIF 2.32 tag type ${type} for ${key} in ${tag}`)
      }
    }
    return output
  }

  /**
   * Converts a HEIC image buffer to a JPEG image, preserving EXIF metadata.
   *
   * @param {ArrayBuffer|Uint8Array|Buffer} inputBuffer - The input buffer containing the HEIC image data.
   * @param {number} [quality=1] - The JPEG quality factor (between 0 and 1). Defaults to 1 (highest quality).
   * @returns {Promise<Blob>} A promise that resolves to a Blob containing the JPEG image data.
   * @throws {Error} If the conversion fails or the input is invalid.
   */
  async convertHeicToJpeg(inputBuffer, quality = 1) {
    // Step 1: Convert HEIC to JPEG (the slow part)
    const blob = new Blob([inputBuffer], {type: 'image/heic'});
    const jpegBlob = await heic2any({
      blob: blob,
      toType: "image/jpeg",
      quality: quality,
    });

    // Step 2: Try to preserve EXIF (if this fails, still return the JPEG)
    try {
      const exr = new Exifr({
        ifd1: true,
        exif: true,
        gps: true,
        translateKeys: false,
        translateValues: false,
        reviveValues: false,
        sanitize: true,
        mergeOutput: false,
      });

      await exr.read(inputBuffer);
      let { ifd0, ifd1, exif, gps } = await exr.parse();

      const filterTags = (field, tags) => {
        if (typeof tags === 'undefined') return {};
        const filteredKeys = Object.values(piexif.TagValues[field]).map(String);
        return Object.keys(tags)
          .filter((key) => filteredKeys.includes(key))
          .reduce((obj, key) => {
            obj[key] = tags[key];
            return obj;
          }, {});
      };

      ifd0 = this.formatTypes('0th', filterTags('ImageIFD', ifd0));
      ifd1 = this.formatTypes('1st', filterTags('ImageIFD', ifd1));
      exif = this.formatTypes('Exif', filterTags('ExifIFD', exif));
      gps = this.formatTypes('GPS', filterTags('GPSIFD', gps));

      const originalOrientation = ifd0[piexif.TagValues.ImageIFD.Orientation];
      ifd0[piexif.TagValues.ImageIFD.Orientation] = 1;
      if (originalOrientation > 4) {
        const xd = exif[piexif.TagValues.ExifIFD.PixelXDimension];
        const yd = exif[piexif.TagValues.ExifIFD.PixelYDimension];
        exif[piexif.TagValues.ExifIFD.PixelXDimension] = yd;
        exif[piexif.TagValues.ExifIFD.PixelYDimension] = xd;
      }

      const exifBytes = piexif.dump({
        '0th': ifd0,
        '1st': ifd1,
        Exif: exif,
        GPS: gps,
      });

      const dataUrl = await this.readBlobAsDataURL(jpegBlob);
      const newDataUrl = piexif.insert(exifBytes, dataUrl);

      const base64 = newDataUrl.split(',')[1];
      const binaryString = atob(base64);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], {type: 'image/jpeg'});
    } catch (exifError) {
      this.LogService.warn('EXIF preservation failed, returning JPEG without EXIF:', exifError);
      return jpegBlob;
    }
  }

  async readBlobAsDataURL(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result); // reader.result contains the data URL
      };

      reader.onerror = (error) => {
        reject(error); // Handle any errors during the read operation
      };

      reader.readAsDataURL(blob); // Start reading the blob as a Data URL
    });
  }
}


export default DocumentService;

m.service('DocumentService', DocumentService);
